import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { formatDisplayDateTime } from "utils/dateFormat";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatMoney } from "utils/utils";
import AccountVerificationChip from "./AccountVerificationChip";
import AccountVerificationAuditLogExpander from "./AccountVerificationAuditLogExpander";

const money = (value) => formatMoney(value || 0);

const verificationScope = (item) => {
  if (!item?.admissionDetailId) {
    return "Account entry";
  }
  const installment = item?.submittedAccountDetail?.admissionDetails?.[0];
  const rawTerm = installment?.terms;
  const term = rawTerm ? `T${String(rawTerm).replace(/^[Tt]/, "")}` : "installment";
  return `${term} - already in account`;
};

const AccountVerificationRequests = ({ onCountChange }) => {
  const navigate = useNavigate();
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
  const [rejectFeedback, setRejectFeedback] = useState("");
  const [verifyDialog, setVerifyDialog] = useState({ open: false, item: null });

  const {
    data: requests = [],
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["account-verification-assigned"],
    queryFn: () =>
      axios.get(`${API_URL}/account-verification/assigned`).then((r) => r.data?.data ?? []),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    onCountChange?.(requests.length);
  }, [onCountChange, requests.length]);

  const openClientAccount = useCallback(
    (item) => {
      if (!item?.accountMasterId) {
        toast.info("No linked account entry was found for this verification.");
        return;
      }
      navigate("/admin/account/add", {
        state: {
          item: {
            hasAccountDetails: true,
            clientAccountId: item.accountMasterId,
            clientData: item.clientId
              ? {
                  id: item.clientId,
                  name: item.clientName,
                }
              : null,
          },
        },
      });
    },
    [navigate]
  );

  const verifyMutation = useMutation({
    mutationFn: (id) => axios.post(`${API_URL}/account-verification/${id}/verify-and-import`),
    onSuccess: () => {
      toast.success("Verified and imported into accounts");
      setVerifyDialog({ open: false, item: null });
      refetch();
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Failed to verify request"
      ),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, feedback }) =>
      axios.post(`${API_URL}/account-verification/${id}/reject`, { feedback }),
    onSuccess: () => {
      toast.success("Verification rejected");
      setRejectDialog({ open: false, id: null });
      setRejectFeedback("");
      refetch();
    },
    onError: () => toast.error("Failed to reject request"),
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "clientName",
        header: "Client",
        size: 180,
      },
      {
        accessorKey: "module",
        header: "Module",
        size: 110,
      },
      {
        accessorKey: "scope",
        header: "Scope",
        size: 180,
        Cell: ({ row }) => verificationScope(row.original),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 160,
        Cell: ({ row }) => {
          const item = row.original;
          const chip = (
            <AccountVerificationChip
              status={item.status}
              rejectionFeedback={item.rejectionFeedback}
            />
          );
          if (item.status !== "IMPORTED") return chip;
          return (
            <Tooltip title="Imported into client account. Click to open this account entry." arrow>
              <span
                role="button"
                tabIndex={0}
                onClick={() => openClientAccount(item)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") openClientAccount(item);
                }}
                className="inline-flex cursor-pointer"
              >
                {chip}
              </span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "sentByName",
        header: "Sent By",
        size: 150,
      },
      {
        accessorKey: "sentAt",
        header: "Sent At",
        size: 150,
        Cell: ({ cell }) => formatDisplayDateTime(cell.getValue()),
      },
      {
        accessorKey: "receiptDocuments",
        header: "Receipts",
        size: 120,
        Cell: ({ row }) => {
          const receipts = row.original.receiptDocuments || [];
          return receipts.length ? `${receipts.length} file(s)` : "-";
        },
      },
      {
        accessorKey: "actions",
        header: "Actions",
        size: 200,
        Cell: ({ row }) => {
          const item = row.original;
          return (
            <Stack direction="row" spacing={1}>
              {item.status === "PENDING" && (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setVerifyDialog({ open: true, item })}
                    disabled={verifyMutation.isLoading}
                  >
                    Verify & Import
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => setRejectDialog({ open: true, id: item.id })}
                  >
                    Reject
                  </Button>
                </>
              )}
              {item.status === "IMPORTED" && (
                <Typography variant="body2" color="text.secondary">
                  Imported
                </Typography>
              )}
              {item.status === "REJECTED" && (
                <Typography variant="body2" color="text.secondary">
                  Rejected
                </Typography>
              )}
            </Stack>
          );
        },
      },
    ],
    [openClientAccount, verifyMutation]
  );

  const table = useMaterialReactTable({
    columns,
    data: requests,
    enableRowNumbers: true,
    enablePagination: true,
    initialState: { density: "compact", showGlobalFilter: true },
    state: { isLoading: isFetching, showProgressBars: isFetching },
    muiTableContainerProps: {
      sx: {
        maxHeight: "65vh",
        minHeight: "65vh",
        overflowY: "auto",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "12px",
      },
    },
    renderDetailPanel: ({ row }) => <VerificationRequestDetail request={row.original} />,
  });

  return (
    <>
      <MaterialReactTable table={table} />
      <Dialog
        open={verifyDialog.open}
        onClose={() => setVerifyDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify and Import</DialogTitle>
        <DialogContent>
          <Typography>Verify this entry and automatically import it into accounts?</Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog({ open: false, item: null })}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => verifyMutation.mutate(verifyDialog.item?.id)}
            disabled={verifyMutation.isLoading}
          >
            {verifyMutation.isLoading ? "Importing…" : "Verify & Import"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={rejectDialog.open}
        onClose={() => setRejectDialog({ open: false, id: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Verification</DialogTitle>
        <DialogContent>
          <TextField
            label="Reason for rejection"
            multiline
            rows={3}
            fullWidth
            required
            value={rejectFeedback}
            onChange={(event) => setRejectFeedback(event.target.value)}
            sx={{ mt: 1 }}
            error={rejectFeedback.trim() === ""}
            helperText={rejectFeedback.trim() === "" ? "Required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, id: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={rejectFeedback.trim() === "" || rejectMutation.isLoading}
            onClick={() =>
              rejectMutation.mutate({ id: rejectDialog.id, feedback: rejectFeedback.trim() })
            }
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const VerificationRequestDetail = ({ request }) => {
  const detail = request?.submittedAccountDetail || {};
  const admissionDetails = detail?.admissionDetails || [];
  const receipts = request?.receiptDocuments || [];
  const linkedInvoices = request?.linkedInvoices || [];

  return (
    <Box sx={{ p: 2, bgcolor: "var(--app-surface-alt)" }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={3} flexWrap="wrap">
          <Typography variant="body2">
            <strong>Agreed:</strong> {money(detail.agreedAmount)}
          </Typography>
          <Typography variant="body2">
            <strong>Cost:</strong> {money(detail.cost)}
          </Typography>
          <Typography variant="body2">
            <strong>Paid:</strong> {money(detail.paidAmount)}
          </Typography>
          <Typography variant="body2">
            <strong>Due:</strong> {money(detail.dueAmount)}
          </Typography>
          <Typography variant="body2">
            <strong>Referral:</strong> {money(detail.referral)}
          </Typography>
        </Stack>

        {receipts.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Receipts
            </Typography>
            <Stack spacing={0.5}>
              {receipts.map((url, index) => (
                <Link key={url} href={url} target="_blank" rel="noreferrer">
                  Receipt {index + 1}
                </Link>
              ))}
            </Stack>
          </Box>
        )}

        {linkedInvoices.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Linked invoices
            </Typography>
            <Stack spacing={0.5}>
              {linkedInvoices.map((invoice, index) => (
                <Link
                  key={`${invoice.invoiceId || "invoice"}-${index}`}
                  href={`/admin/invoice/edit/${invoice.invoiceId}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Invoice #{invoice.invoiceId}
                  {invoice.term ? ` - ${invoice.term}` : ""}
                  {invoice.paymentStatus ? ` (${invoice.paymentStatus})` : ""}
                </Link>
              ))}
            </Stack>
          </Box>
        )}

        {admissionDetails.length > 0 && (
          <Box sx={{ overflowX: "auto" }}>
            <table className="min-w-full text-sm bg-app-surface border border-app-border">
              <thead>
                <tr className="bg-app-surface-muted">
                  <th className="text-left p-2">Term</th>
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Fee</th>
                  <th className="text-left p-2">Material</th>
                  <th className="text-left p-2">Paid</th>
                  <th className="text-left p-2">Due</th>
                  <th className="text-left p-2">Referral</th>
                  <th className="text-left p-2">Discount</th>
                </tr>
              </thead>
              <tbody>
                {admissionDetails.map((item, index) => (
                  <tr key={item.id || item.uuid || index} className="border-t border-app-border">
                    <td className="p-2">{item.terms || index + 1}</td>
                    <td className="p-2">{item.date || "-"}</td>
                    <td className="p-2">{money(item.perSemFee)}</td>
                    <td className="p-2">{money(item.materialFee)}</td>
                    <td className="p-2">{money(item.paidAmount)}</td>
                    <td className="p-2">{money(item.dueAmount)}</td>
                    <td className="p-2">{money(item.referral)}</td>
                    <td className="p-2">{money(item.discount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}

        <AccountVerificationAuditLogExpander auditLogs={request?.auditLogs || []} />
      </Stack>
    </Box>
  );
};

export default AccountVerificationRequests;
